<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Trash2 } from '@lucide/vue'
import type { Comic } from '@tanko/shared'
import { Button, ErrorMessage, Field, FieldGroup, Fieldset, IconButton, InlineAlert, Input, Label } from 'catalyst-vue'
import { useLibraryStore } from '../../stores/library'
import { useCloudLibraryStore } from '../../stores/cloud-library'
import LibraryOverlay from './library-overlay.vue'
import CategoryPickerField from './category-picker-field.vue'
import { saveComicMetadata } from '../../lib/category-service'
import { errorMessage } from '../../lib/fs-access'
import { useComicRemoval } from '../../lib/comic-removal-flow'
import { useControlSize, useIconControlSize } from '../../lib/library-layout'
import { isBackedUp, isRegistered } from '../../lib/comic-sync'

const props = defineProps<{ comic?: Comic }>()
const emit = defineEmits<{ close: [] }>()
const store = useLibraryStore()
const cloud = useCloudLibraryStore()
const { removeComic } = useComicRemoval()
const controlSize = useControlSize()
const iconSize = useIconControlSize()
const title = ref('')
const author = ref('')
const selected = ref<string[]>([])
const saving = ref(false)
/** 分類存在雲端目錄，因此尚未註冊的作品不能指派分類。 */
const registered = computed(() => !!props.comic && isRegistered(props.comic))
/**
 * 已註冊的作品其 metadata 由雲端目錄保管，登入失效時無法送出。
 * 尚未註冊的作品只寫本地，因此不受影響——標題與作者會在註冊時一併帶上。
 */
const blockedByAuth = computed(() => registered.value && cloud.authenticationRequired)
const error = ref('')
watch(() => props.comic?.id, () => {
  title.value = props.comic?.title ?? ''
  author.value = props.comic?.author ?? ''
  selected.value = store.assignments.filter(a => a.comicId === props.comic?.id).map(a => a.categoryId)
  error.value = ''
}, { immediate: true })
async function save() {
  if (!props.comic || saving.value) return
  saving.value = true
  error.value = ''
  try {
    // 分類的真相在雲端目錄，因此整理一律先送出再更新本地鏡像。
    await saveComicMetadata(props.comic, { title: title.value, author: author.value, categoryIds: selected.value })
    // 送出去的同時雲端那份清單與分類計數就過期了；不標記的話切到雲端分頁還是舊值，
    // 看起來像是本地改了但沒有同步。
    cloud.invalidate()
    emit('close')
  }
  catch (cause) { error.value = errorMessage(cause) }
  finally { saving.value = false }
}
/**
 * 移除作品。已備份的作品多一條出口：連雲端備份一起刪。
 *
 * 預設是只從書庫移除——本地移除的語意一直是「從書庫移除，硬碟檔案不動」，而雲端那份
 * 可能是唯一的備份。但也不能不問：只移除之後就沒有任何東西指向雲端那份了，同一個資料夾
 * 之後重新加入書庫會再上傳第二份，舊的那份永遠沒人認領。
 */
async function remove() {
  const comic = props.comic
  if (!comic || saving.value) return
  saving.value = true
  error.value = ''
  try { if (await removeComic(comic)) emit('close') }
  catch (cause) { error.value = errorMessage(cause) }
  finally { saving.value = false }
}
function close() { if (!saving.value) emit('close') }
</script>

<template>
  <LibraryOverlay :open="!!comic" title="整理作品" description="修改書名、作者與分類，不會更動資料夾名稱。" :pending="saving" @close="close">
      <form id="metadata-form" @submit.prevent="save">
        <Fieldset :disabled="saving">
          <FieldGroup>
            <Field><Label>書名</Label><Input v-model="title" required :invalid="!title.trim()" /><ErrorMessage v-if="!title.trim()">請填寫書名，才能儲存作品。</ErrorMessage></Field>
            <Field><Label>作者</Label><Input v-model="author" placeholder="尚未填寫" /></Field>
            <CategoryPickerField v-model="selected" :categories="store.categories" :disabled="saving || !registered || blockedByAuth" />
            <ErrorMessage v-if="!registered">這本作品還在加入雲端目錄，完成後才能指派分類。書名與作者仍可修改。</ErrorMessage>
            <ErrorMessage v-else-if="blockedByAuth">雲端登入已失效，分類與整理暫時無法變更。重新登入後即可儲存。</ErrorMessage>
          </FieldGroup>
        </Fieldset>
        <InlineAlert v-if="error" tone="error" class="mt-4">{{ error }}</InlineAlert>
      </form>
      <!--
        整理只負責書名、作者、分類與移除。備份的入口在作品卡片的 more actions 選單與
        書庫頁的備份佇列面板，不在這裡——藏在對話框裡的單本入口正是整個藏書備份不完的原因。
      -->
      <div class="metadata-secondary-actions">
        <p class="muted-copy">{{ comic && isBackedUp(comic) ? '這本作品已連結私人雲端。' : '這本作品尚未備份到私人雲端；備份在書庫頁的備份佇列裡進行。' }}</p>
      </div>
    <template #actions>
      <IconButton color="danger" :size="iconSize" class="overlay-destructive" :disabled="saving" :label="`從書庫移除「${comic?.title ?? ''}」`" @click="remove"><Trash2 :size="17" aria-hidden="true" /></IconButton>
      <Button outline :size="controlSize" :disabled="saving" @click="close">取消</Button>
      <Button color="primary" :size="controlSize" type="submit" form="metadata-form" :disabled="saving || !title.trim() || blockedByAuth">{{ saving ? '正在儲存…' : '儲存' }}</Button>
    </template>
  </LibraryOverlay>
</template>
